import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
// Permission confirmations will be handled via API

enum ChangeRequestStatus {
  PENDING_EMAIL = 'pending_email',
  EMAIL_SENT = 'email_sent',
  CONFIRMED = 'confirmed',
  APPLIED = 'applied',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  FAILED = 'failed'
}

interface PermissionChangeRequest {
  id: string;
  status: ChangeRequestStatus;
  change: any;
  confirmationToken: string;
  expiresAt: Date;
  createdAt: Date;
}

// Stub functions - will be replaced with API calls
const getChangeRequestByToken = async (token: string): Promise<PermissionChangeRequest | null> => {
  // TODO: Implement API call
  return null;
};

const confirmChangeRequest = async (token: string): Promise<boolean> => {
  // TODO: Implement API call
  return false;
};

export default function ConfirmChange() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState<PermissionChangeRequest | null>(null);
  const [error, setError] = useState<string>('');
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    loadRequest();
  }, [token]);

  const loadRequest = async () => {
    if (!token) {
      setError('Invalid confirmation link');
      setLoading(false);
      return;
    }

    try {
      const changeRequest = await getChangeRequestByToken(token);

      if (!changeRequest) {
        setError('Invalid or expired confirmation link');
      } else if (changeRequest.status === ChangeRequestStatus.EXPIRED) {
        setError('This confirmation link has expired');
      } else if (changeRequest.status === ChangeRequestStatus.CONFIRMED ||
                 changeRequest.status === ChangeRequestStatus.APPLIED) {
        setError('This change has already been confirmed');
      } else if (new Date(changeRequest.expiresAt).getTime() < Date.now()) {
        setError('This confirmation link has expired');
      } else {
        setRequest(changeRequest);
      }
    } catch (err) {
      setError('Failed to load confirmation request');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!token) return;

    setConfirming(true);
    try {
      const success = await confirmChangeRequest(token);
      if (success) {
        setConfirmed(true);
      } else {
        setError('Failed to confirm changes. The link may have expired.');
      }
    } catch (err) {
      setError('An error occurred while confirming the changes');
      console.error(err);
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3
      }}>
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Confirmation Failed
              </Typography>
              <Typography color="text.secondary" paragraph>
                {error}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{ mt: 2 }}
              >
                Go to Dashboard
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (confirmed) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3
      }}>
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Changes Confirmed!
              </Typography>
              <Typography color="text.secondary" paragraph>
                The permission changes have been successfully applied.
              </Typography>
              <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
                All admins have been notified of these changes via email.
                Changes can be rolled back within 7 days if needed.
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
              >
                Go to Dashboard
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!request) return null;

  const { change } = request;
  const totalChanges = change.changes.added.length + change.changes.removed.length;
  const expiresIn = Math.max(0, Math.floor((new Date(request.expiresAt).getTime() - Date.now()) / 60000));

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      p: 3
    }}>
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ textAlign: 'center' }}>
            Confirm Permission Changes
          </Typography>

          <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon />
              <Typography>
                This link expires in {expiresIn} minutes
              </Typography>
            </Box>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Agent: {change.agent}
            </Typography>
            <Typography color="text.secondary">
              Requested by: {change.requestedBy}
            </Typography>
            <Typography color="text.secondary">
              Total changes: {totalChanges}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {change.changes.added.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="success.main" gutterBottom>
                Services to be ADDED:
              </Typography>
              <List dense>
                {change.changes.added.map((service: string) => (
                  <ListItem key={service}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AddIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {change.changes.removed.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="error.main" gutterBottom>
                Services to be REMOVED:
              </Typography>
              <List dense>
                {change.changes.removed.map((service: string) => (
                  <ListItem key={service}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <RemoveIcon color="error" />
                    </ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Alert severity="info" sx={{ mb: 3 }}>
            By confirming, these changes will be applied immediately to the production environment.
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              disabled={confirming}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleConfirm}
              disabled={confirming}
              sx={{ minWidth: 200 }}
            >
              {confirming ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Applying Changes...
                </>
              ) : (
                'Confirm Changes'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
